import type { NetworkDashboardAvailableStore } from '@gozoki/api-types'
import { computeDefaultComparisonPeriod } from '@gozoki/api-types/src/network-comparison'
import { DateTime } from 'luxon'
import { resolveAllTimeStartIso } from '~/components/dashboards/filters/all-time-range'
import { presetDays } from '~/components/dashboards/filters/date-presets'
import { DASHBOARD_ALL_TIME_START_ISO } from '~/components/dashboards/filters/types'
import type { DateRangePreset } from '~/components/ui/date-range-picker'

// Presets réseau (mêmes valeurs que `HR_DATE_PRESETS` côté RH) : le picker de la modale
// n'affiche que `DASHBOARD_DATE_PRESETS` (sans « Aujourd'hui »), mais l'URL peut porter
// n'importe quel slug standard.
export const STORE_DASHBOARD_DATE_PRESETS = [
  'yesterday',
  'today',
  '7d',
  '30d',
  '3m',
  '12m',
  'mtd',
  'ytd',
  'all',
] as const satisfies readonly DateRangePreset[]

export interface DateWindow {
  start: string
  end: string
}

export interface DashboardWindows {
  period: DateWindow
  comparison: DateWindow
}

export interface StoreDashboardWindowSearch {
  preset?: DateRangePreset
  startDate?: string
  endDate?: string
  /** `custom` : compStartDate/compEndDate font foi. `auto` (défaut) : comparaison calendaire. */
  comparableMode?: 'auto' | 'custom'
  compStartDate?: string
  compEndDate?: string
}

function rangeForPreset(preset: DateRangePreset, today: DateTime): DateWindow {
  const { start, end } = presetDays(preset, today)
  return { start: start ?? DASHBOARD_ALL_TIME_START_ISO, end }
}

/** Fenêtre de période brute (sentinelle « Toujours » non résolue) : URL si présente, sinon preset. */
export function resolveStoreDashboardPeriodWindow(
  search: Pick<StoreDashboardWindowSearch, 'preset' | 'startDate' | 'endDate'>,
  today: DateTime,
): DateWindow {
  if (search.startDate !== undefined && search.endDate !== undefined) {
    return { start: search.startDate, end: search.endDate }
  }
  return rangeForPreset(search.preset ?? '7d', today)
}

/**
 * Comparaison calendaire automatique d'une période — réimplémentation locale de
 * `buildAutoCalendarComparisonRange` (`~/components/dashboards/filters/date-presets`) sur des
 * `DateWindow` (ISO) plutôt que des `Date`. « Toujours » (sentinelle) n'a pas de fenêtre
 * précédente sensée : comme le picker réseau, on renvoie la période inchangée (le point
 * d'appel résout alors la sentinelle une seule fois pour les deux fenêtres, cf.
 * `resolveStoreDashboardWindows`).
 */
export function buildStoreDashboardAutoComparisonWindow(period: DateWindow): DateWindow {
  if (period.start === DASHBOARD_ALL_TIME_START_ISO) {
    return { ...period }
  }
  const { compStart, compEnd } = computeDefaultComparisonPeriod(period.start, period.end)
  return { start: compStart, end: compEnd }
}

/**
 * Résout `{ period, comparison }` pour l'affichage/les requêtes : période = URL si présente
 * (`startDate`/`endDate`, sentinelle « Toujours » résolue via `availableStores`/`storeIds`),
 * sinon preset (défaut `7d`). Comparaison = dates explicites en mode `custom`, sinon fenêtre
 * calendaire automatique — qui hérite du même début résolu que la période quand celle-ci part
 * de « Toujours » (pas de fenêtre « précédente » sensée dans ce cas).
 */
export function resolveStoreDashboardWindows(
  search: StoreDashboardWindowSearch,
  today: DateTime,
  availableStores: NetworkDashboardAvailableStore[] = [],
  effectiveStoreIds: number[] = [],
): DashboardWindows {
  const rawPeriod = resolveStoreDashboardPeriodWindow(search, today)
  const resolvedStart =
    rawPeriod.start === DASHBOARD_ALL_TIME_START_ISO
      ? resolveAllTimeStartIso(
          availableStores,
          { storeIds: effectiveStoreIds, storeAgeFilter: 'default' },
          rawPeriod.end,
        )
      : rawPeriod.start
  const period: DateWindow = { start: resolvedStart, end: rawPeriod.end }

  if (search.comparableMode === 'custom' && search.compStartDate && search.compEndDate) {
    return { period, comparison: { start: search.compStartDate, end: search.compEndDate } }
  }

  const rawComparison = buildStoreDashboardAutoComparisonWindow(rawPeriod)
  const comparison = rawComparison.start === DASHBOARD_ALL_TIME_START_ISO ? period : rawComparison
  return { period, comparison }
}

/**
 * Analogue de `periodQueriesEnabled` (`useHrPeriodWindow`) côté RH : tant que la période résolue
 * est encore la sentinelle « Toujours » (résolution en attente de `availableStores`, cf.
 * `resolveAllTimeStartIso`) ET que la liste des boutiques n'est pas encore
 * chargée, les requêtes datées ne doivent PAS partir — elles enverraient sinon
 * `startDate = DASHBOARD_ALL_TIME_START_ISO` (2020-01-01) au lieu de la vraie date de
 * première vente. Se déclenche typiquement avec une boutique unique sélectionnée : `storeIds`
 * est alors disponible immédiatement depuis l'URL, sans attendre `api.stores.index`.
 */
export function storeDashboardPeriodQueriesEnabled(
  periodStartIso: string,
  storesSettled: boolean,
): boolean {
  return periodStartIso !== DASHBOARD_ALL_TIME_START_ISO || storesSettled
}

const shortDay = (iso: string) => DateTime.fromISO(iso).toFormat('dd/MM')

export const formatDayLong = (iso: string): string => DateTime.fromISO(iso).toFormat('dd/MM/yyyy')

export const formatWindowShort = (window: DateWindow): string =>
  `${shortDay(window.start)} → ${shortDay(window.end)}`

export const formatWindowLong = (window: DateWindow): string =>
  `${formatDayLong(window.start)} → ${formatDayLong(window.end)}`

export const windowToDates = (window: DateWindow): { start: Date; end: Date } => ({
  start: DateTime.fromISO(window.start).startOf('day').toJSDate(),
  end: DateTime.fromISO(window.end).endOf('day').toJSDate(),
})
